/**
 * CourseConversationModal — capture in-flight course experience as bullets.
 *
 * Opens from a course widget on the dashboard. Lets the user pick (or skip)
 * a topical prompt — "SQL Project", "Final assignment" — that seeds the
 * conversation, then drives the same /conversations-sync + /messages API
 * the ScopeBullets modal uses. Bullets surface as the chat surfaces
 * specifics, ready to land under the user's Education entry for the program.
 *
 *   ┌─ This quarter at Foster ─────────────────────[X]─┐
 *   │ I S 500                                          │
 *   │ Data Management for Analytics                    │
 *   ├──────────────────────────────────────────────────┤
 *   │ What are you working on in this class?           │
 *   │ Click a prompt — or just start typing.           │
 *   │ [SQL Project]  [Final analytics project]         │
 *   │ [Group case]  [Skills you're picking up]         │
 *   ├──────────────────────────────────────────────────┤
 *   │ 💬 Conversation                                   │
 *   │ [messages…]                                      │
 *   │ [Type your response…]                  [→]       │
 *   ├──────────────────────────────────────────────────┤
 *   │ ✨ Bullets we've drafted (N)                      │
 *   └──────────────────────────────────────────────────┘
 *
 * The conversation is fresh per (course, prompt) pair — picking a
 * different prompt resets the chat so the agent doesn't blend topics.
 * "Just start typing" keeps the original blank-canvas option.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';

import { SAGE, CREAM } from '../../theme';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';
import { useIsDemoUser, findFixture } from '../../utils/demoMode';
import { COURSE_FIXTURES, DEMO_USER_REPLY_ACK } from '../../data/demo_conversations';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const buildTopic = (course, prompt) => {
  if (prompt) return `Tell me about your ${prompt} for ${course.code} — ${course.title}.`;
  return `Capture your experience in ${course.code} — ${course.title}.`;
};

const CourseConversationModal = ({ open, onClose, course, programLabel = 'Foster MBA' }) => {
  const isDemoUser = useIsDemoUser();
  const navigate = useNavigate();
  const goToCourseDetail = () => {
    if (!course) return;
    onClose();
    navigate(`/courses/${encodeURIComponent(course.code)}`);
  };
  const [conversationId, setConversationId] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [input, setInput] = useState('');
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [usedFixture, setUsedFixture] = useState(false);
  const messagesEndRef = useRef(null);

  // Reset state when modal opens / course changes
  useEffect(() => {
    if (!open || !course) return;
    setConversationId(null);
    setActivePrompt(null);
    setMessages([]);
    setBullets([]);
    setInput('');
    setError(null);
    setUsedFixture(false);
  }, [open, course]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const startConversation = async (prompt) => {
    if (!course) return;
    setActivePrompt(prompt || null);
    setMessages([]);
    setBullets([]);
    setConversationId(null);
    setError(null);
    setUsedFixture(false);

    // Demo-fixture path: instant load when (course, prompt) matches a baked
    // conversation. Falls through to real backend when no fixture is found.
    if (isDemoUser && prompt) {
      const fixture = findFixture(COURSE_FIXTURES, `${course.code}::${prompt}`);
      if (fixture) {
        setMessages(fixture.messages || []);
        setBullets(fixture.bullets || []);
        setUsedFixture(true);
        return;
      }
    }

    setStarting(true);
    try {
      const resp = await fetch(`${API_URL}/api/profile/enhancement/conversations-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          topic: buildTopic(course, prompt),
          context: {
            source: 'course_experience',
            program: programLabel,
            course_code: course.code,
            course_title: course.title,
            prompt: prompt || null,
          },
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setConversationId(data.conversation_id || null);
      setMessages(data.messages || []);
      setBullets(data.bullets || []);
    } catch (err) {
      setError('Could not start the conversation. Try again in a moment.');
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const optimistic = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    // Demo-fixture path: append user message + a canned ack, no API roundtrip.
    if (usedFixture && !conversationId) {
      setMessages((prev) => [...prev, optimistic, DEMO_USER_REPLY_ACK(text)]);
      setInput('');
      return;
    }

    // Lazy-start: if user types without picking a prompt, kick off a generic
    // conversation now and queue their message after the agent's opener.
    if (!conversationId) {
      await startConversation(null);
      // The conversation kick-off doesn't auto-send the user's message —
      // they can hit send again now that the convo is live.
      return;
    }
    setSending(true);
    setError(null);
    setMessages((prev) => [...prev, optimistic]);
    setInput('');
    try {
      const resp = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ content: text }),
        }
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setMessages(data.messages || []);
      setBullets(data.bullets || []);
    } catch (err) {
      setError('Could not send. Try again.');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!course) return null;
  const prompts = course.prompts || [];
  const showPromptPicker = !conversationId && !starting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { bgcolor: CREAM[50], borderRadius: 3, maxHeight: '90vh' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3, pt: 2.5, pb: 2,
          borderBottom: `1px solid ${SAGE[200]}`,
          display: 'flex', alignItems: 'flex-start', gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: F, fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: 1.6, textTransform: 'uppercase', color: SAGE[500],
              mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75,
            }}
          >
            <SchoolIcon sx={{ fontSize: 13 }} />
            This quarter at {programLabel}
            {course.quarter ? ` · ${course.quarter}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: F, fontSize: '0.78rem', fontWeight: 700,
                color: SAGE[600], letterSpacing: 0.4,
              }}
            >
              {course.code}
            </Typography>
            <Box
              onClick={goToCourseDetail}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                fontFamily: F, fontSize: '0.7rem', fontWeight: 600,
                color: SAGE[500], cursor: 'pointer',
                '&:hover': { color: SAGE[800] },
              }}
            >
              View course details
              <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          <Typography
            sx={{
              fontFamily: SERIF, fontSize: '1.2rem', fontWeight: 500,
              color: SAGE[800], lineHeight: 1.3, mt: 0.25,
            }}
          >
            {course.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: SAGE[500], mt: 0.25 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Prompt picker — only shown until a conversation starts */}
      {showPromptPicker && (
        <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${SAGE[100]}`, bgcolor: `${SAGE[100]}55` }}>
          <Typography
            sx={{
              fontFamily: F, fontSize: '0.85rem', fontWeight: 600,
              color: SAGE[800], mb: 0.5,
            }}
          >
            What are you working on in this class?
          </Typography>
          <Typography
            sx={{
              fontFamily: F, fontSize: '0.74rem', color: SAGE[500],
              fontStyle: 'italic', mb: 1.5,
            }}
          >
            Click a prompt to seed the conversation — or just start typing below.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {prompts.map((p) => (
              <Box
                key={p}
                onClick={() => startConversation(p)}
                sx={{
                  px: 1.5, py: 0.85, borderRadius: 2,
                  bgcolor: '#fff', border: `1.5px solid ${SAGE[300]}`,
                  color: SAGE[700], cursor: 'pointer',
                  fontFamily: F, fontSize: '0.78rem', fontWeight: 600,
                  transition: 'all 0.18s',
                  '&:hover': { bgcolor: SAGE[700], color: CREAM[50], borderColor: SAGE[700] },
                }}
              >
                {p}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Active topic eyebrow once started */}
      {activePrompt && conversationId && (
        <Box sx={{ px: 3, py: 1.25, borderBottom: `1px solid ${SAGE[100]}`, bgcolor: `${SAGE[100]}55` }}>
          <Typography sx={{ fontFamily: F, fontSize: '0.74rem', color: SAGE[500] }}>
            <Box component="span" sx={{ fontWeight: 700, color: SAGE[600] }}>Topic:</Box> {activePrompt}
            <Box
              component="span"
              onClick={() => {
                // Reset to picker so user can switch topics mid-modal
                setConversationId(null);
                setActivePrompt(null);
                setMessages([]);
                setBullets([]);
              }}
              sx={{
                ml: 1.5, color: SAGE[500], cursor: 'pointer', textDecoration: 'underline',
                fontSize: '0.72rem', '&:hover': { color: SAGE[800] },
              }}
            >
              switch topic
            </Box>
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mx: 3, mt: 2, px: 2, py: 1, bgcolor: '#f0e0e0', color: '#8a3a3a', borderRadius: 1.5, fontFamily: F, fontSize: '0.78rem' }}>
          {error}
        </Box>
      )}

      {/* Conversation */}
      <Box
        sx={{
          flex: 1, minHeight: 240, maxHeight: 360,
          display: 'flex', flexDirection: 'column',
          borderBottom: `1px solid ${SAGE[100]}`,
        }}
      >
        <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: SAGE[500] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
            Conversation
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {starting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2 }}>
              <CircularProgress size={14} sx={{ color: SAGE[500] }} />
              <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic' }}>
                Starting the conversation…
              </Typography>
            </Box>
          ) : messages.length === 0 && !conversationId ? (
            <Typography sx={{ fontFamily: F, fontSize: '0.82rem', color: SAGE[500], fontStyle: 'italic', py: 1 }}>
              Pick a prompt above, or type your first response below to kick things off.
            </Typography>
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
          {sending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start', py: 0.5 }}>
              <CircularProgress size={12} sx={{ color: SAGE[500] }} />
              <Typography sx={{ fontFamily: F, fontSize: '0.74rem', color: SAGE[500], fontStyle: 'italic' }}>
                Thinking…
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
        <Box sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1,
              bgcolor: '#fff', border: `1.5px solid ${SAGE[200]}`, borderRadius: 1.5,
              p: 0.5, transition: 'border-color 0.18s',
              '&:focus-within': { borderColor: SAGE[500] },
            }}
          >
            <TextField
              fullWidth multiline maxRows={4}
              placeholder={conversationId ? 'Tell the story — what did you do, what did you ship, what changed?' : 'Type to start a free-form conversation…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || starting}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { px: 1.5, py: 0.75, fontSize: '0.88rem', fontFamily: F, color: SAGE[800] },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || sending || starting}
              sx={{
                bgcolor: SAGE[700], color: CREAM[50], borderRadius: 1, px: 1.5, py: 1,
                '&:hover': { bgcolor: SAGE[800] },
                '&.Mui-disabled': { bgcolor: SAGE[200], color: CREAM[100] },
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Bullets */}
      <Box sx={{ px: 3, py: 2.5, bgcolor: `${CREAM[100]}80`, maxHeight: 240, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: SAGE[500] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
            Bullets we've drafted ({bullets.length})
          </Typography>
        </Box>
        {bullets.length === 0 ? (
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic' }}>
            Bullets will land here as the conversation surfaces specifics — what you built, what changed, what stuck. They'll attach to {course.code} under your {programLabel} entry.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {bullets.map((b) => <LightBulletCard key={b.id} bullet={b} />)}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box sx={{ maxWidth: '88%', alignSelf: isUser ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={{
          px: 1.75, py: 1.25, borderRadius: 2,
          bgcolor: isUser ? SAGE[700] : '#fff',
          color: isUser ? CREAM[50] : SAGE[800],
          border: isUser ? 'none' : `1px solid ${SAGE[200]}`,
          fontFamily: F, fontSize: '0.85rem', lineHeight: 1.5,
          whiteSpace: 'pre-line',
          borderBottomRightRadius: isUser ? 4 : 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
        }}
      >
        {message.content}
      </Box>
    </Box>
  );
};

const LightBulletCard = ({ bullet }) => {
  const total = bullet.scores ? Object.values(bullet.scores).reduce((a, b) => a + b, 0) : null;
  const tone = total == null ? { label: 'draft', color: SAGE[500] }
    : total >= 13 ? { label: 'strong', color: '#3d7a4a' }
    : total >= 10 ? { label: 'good', color: SAGE[600] }
    : total >= 7  ? { label: 'fair', color: '#c4956a' }
                  : { label: 'rough', color: '#c48a8a' };
  return (
    <Box
      sx={{
        bgcolor: '#fff', borderRadius: 2, border: `1px solid ${SAGE[200]}`,
        px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {bullet.type && (
          <Box sx={{ fontFamily: F, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', px: 0.85, py: 0.15, borderRadius: 0.75, bgcolor: SAGE[100], color: SAGE[600] }}>
            {bullet.type}
          </Box>
        )}
        <Box sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', px: 0.85, py: 0.15, borderRadius: 0.75, bgcolor: `${tone.color}18`, color: tone.color }}>
          {tone.label}
        </Box>
      </Box>
      <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[800], lineHeight: 1.5 }}>
        {bullet.text}
      </Typography>
    </Box>
  );
};

export default CourseConversationModal;
